import { useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Field, Input, OptionTile } from "../ui";
import { escrowService, type EscrowChoice, type EscrowView } from "../../api/services/escrow.service";
import { apiMessage } from "./escrowShared";

interface EscrowChoiceFormProps {
  view: EscrowView;
  onDone: (next: EscrowView) => void;
  /** Extra button beside submit (the pop-up's "Decide later") */
  secondary?: ReactNode;
}

/**
 * PRD 9.1 / FR-79: the seller's escrow and title choice. The wording comes from admin settings
 * (neutral, pending RESPA counsel review), never from this file.
 */
export default function EscrowChoiceForm({ view, onDone, secondary }: EscrowChoiceFormProps) {
  const [choice, setChoice] = useState<EscrowChoice | null>(view.status === "terms_sent" ? "own" : null);
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);
  const w = view.wording;

  const submit = useMutation({
    mutationFn: () =>
      escrowService.choose(view.bidId, {
        choice: choice!,
        ...(choice === "own"
          ? {
              company: company.trim() || undefined,
              name: name.trim() || undefined,
              email: email.trim(),
              phone: phone.trim() || undefined,
            }
          : {}),
      }),
    onSuccess: onDone,
    onError: (err) => setError(apiMessage(err, "Couldn't save your choice")),
  });

  const ready = choice !== null && (choice !== "own" || /\S+@\S+\.\S+/.test(email.trim()));
  const id = `escrow-${view.bidId}`;

  return (
    <form
      className="space-y-3"
      data-testid="escrow-choice-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) submit.mutate();
      }}
    >
      <p className="text-sm text-gray-700">{w.body}</p>
      <div className="space-y-2" role="radiogroup" aria-label={w.title}>
        <OptionTile
          name={`${id}-choice`}
          checked={choice === "partner"}
          disabled={!view.partner}
          onChange={() => setChoice("partner")}
          title={w.partnerLabel}
          description={view.partner ? view.partner.name : "No title company in your network"}
        />
        <OptionTile
          name={`${id}-choice`}
          checked={choice === "own"}
          onChange={() => setChoice("own")}
          title={w.ownLabel}
        />
        {view.status !== "terms_sent" && (
          <OptionTile
            name={`${id}-choice`}
            checked={choice === "terms"}
            onChange={() => setChoice("terms")}
            title={w.termsLabel}
          />
        )}
      </div>
      {choice === "own" && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Escrow / title company" htmlFor={`${id}-company`}>
            <Input id={`${id}-company`} maxLength={200} value={company} onChange={(e) => setCompany(e.target.value)} />
          </Field>
          <Field label="Contact name" htmlFor={`${id}-name`}>
            <Input id={`${id}-name`} maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Contact email" htmlFor={`${id}-email`}>
            <Input
              id={`${id}-email`}
              type="email"
              required
              maxLength={255}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label="Contact phone (optional)" htmlFor={`${id}-phone`}>
            <Input id={`${id}-phone`} maxLength={40} value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
        </div>
      )}
      {choice && choice !== "terms" && (
        <p className="text-meta text-gray-500">
          We'll email them the accepted terms, both parties' contact details and the referral fee, if any. Entity
          documents are never included.
        </p>
      )}
      {error && <Alert variant="error" title={error} />}
      <div className="flex justify-end gap-2">
        {secondary}
        <Button type="submit" variant="brand" disabled={!ready} loading={submit.isPending}>
          {choice === "terms" ? "Email me the terms" : "Open escrow"}
        </Button>
      </div>
    </form>
  );
}
